import { derived, writable } from 'svelte/store';
import { adminDashboardService } from '../lib/api/admin/dashboard.service';

export interface AdminPendingActionsState {
  pendingPayoutsCount: number;
  pendingPayoutsValue: number;
  pendingConfirmationCount: number;
  pendingConfirmationValue: number;
  unconfirmedBankDetailsCount: number;
  charitiesInvitedCount: number;
  employersInvitedCount: number;
  loading: boolean;
  loaded: boolean;
}

const initialState: AdminPendingActionsState = {
  pendingPayoutsCount: 0,
  pendingPayoutsValue: 0,
  pendingConfirmationCount: 0,
  pendingConfirmationValue: 0,
  unconfirmedBankDetailsCount: 0,
  charitiesInvitedCount: 0,
  employersInvitedCount: 0,
  loading: false,
  loaded: false,
};

export const adminPendingActions = writable<AdminPendingActionsState>(initialState);

export const hasAdminPendingPayouts = derived(
  adminPendingActions,
  ($state) => $state.pendingPayoutsCount > 0 || $state.pendingPayoutsValue > 0
);

export const hasAdminPendingConfirmation = derived(
  adminPendingActions,
  ($state) => $state.pendingConfirmationCount > 0 || $state.pendingConfirmationValue > 0
);

export const hasAdminUnconfirmedBankDetails = derived(
  adminPendingActions,
  ($state) => $state.unconfirmedBankDetailsCount > 0
);

export function setAdminPendingActions(summary: {
  pendingPayoutsCount?: number;
  pendingPayoutsValue?: number;
  pendingConfirmationCount?: number;
  pendingConfirmationValue?: number;
  unconfirmedBankDetailsCount?: number;
  charitiesInvitedCount?: number;
  employersInvitedCount?: number;
  awaitingL2gPayout?: number;
  awaitingCharityConfirmation?: number;
}): void {
  adminPendingActions.set({
    pendingPayoutsCount:
      summary.pendingPayoutsCount ?? summary.awaitingL2gPayout ?? 0,
    pendingPayoutsValue: summary.pendingPayoutsValue ?? 0,
    pendingConfirmationCount:
      summary.pendingConfirmationCount ?? summary.awaitingCharityConfirmation ?? 0,
    pendingConfirmationValue: summary.pendingConfirmationValue ?? 0,
    unconfirmedBankDetailsCount: summary.unconfirmedBankDetailsCount ?? 0,
    charitiesInvitedCount: summary.charitiesInvitedCount ?? 0,
    employersInvitedCount: summary.employersInvitedCount ?? 0,
    loading: false,
    loaded: true,
  });
}

export async function refreshAdminPendingActions(): Promise<void> {
  adminPendingActions.update((state) => ({ ...state, loading: true }));

  try {
    const result = await adminDashboardService.getDashboardData(1);
    if (result.success && result.data?.summary) {
      setAdminPendingActions(result.data.summary);
      return;
    }
  } catch (error) {
    console.error('Failed to load admin pending action stats:', error);
  }

  adminPendingActions.update((state) => ({
    ...state,
    loading: false,
    loaded: true,
  }));
}

export function clearAdminPendingActions(): void {
  adminPendingActions.set(initialState);
}
